import { capitalize } from "src/apiService/modules/analyticsGroupings";
import { createSelector } from "reselect";
import { AppState } from "..";
import { getEffectiveTimeZoneFromUser } from "src/utils/timezone";
import type {
  TableConfiguration,
  AnalyticsGroupingItem,
  FeatureFlag,
  StoredDateRange,
} from "src/interfaces/systemState.interface";
import {
  getClampStartDate,
  getDefaultDateRangePeriod,
  resolveStoredDateRange,
  type ResolvedDateRange,
} from "src/utils/timeFilter";
import { defaultLightPalette, defaultDarkPalette } from "src/theme";

const getSystemState = (state: AppState) => state.system;

export const isLoggedInSelector = createSelector(
  getSystemState,
  (system) => system.loggedIn
);
export const loadingSelector = createSelector(
  getSystemState,
  (system) => system.loading
);

export const authorizingSelector = createSelector(
  getSystemState,
  (system) => system.authorizing
);
export const paymentMethodSelector = createSelector(
  getSystemState,
  (system) => !!system.user && (!system.user.pm_id || !system.user.plan)
);
export const selectUser = createSelector(
  getSystemState,
  (system) => system.user
);
export const userPlanSelector = createSelector(
  getSystemState,
  (system) => system.user?.plan
);
export const userIncomeTaxRateSelector = createSelector(
  getSystemState,
  (system) => system.user?.income_tax_rate
);
export const userCustomerIdSelector = createSelector(
  getSystemState,
  (system) => system.user?.customer_id
);
export const systemError = createSelector(
  getSystemState,
  (system) => system.error
);
export const ebayIntegrationSelector = createSelector(
  getSystemState,
  (system) => system.ebayIntegration
);
export const tapfiliateSelector = createSelector(
  getSystemState,
  (system) => system.signupSuccess
);
export const expenseRulesSelector = createSelector(selectUser, (user) =>
  (user?.expense_rules || [])
    .map((r) => JSON.parse(r))
    .filter((r) => r.plaid_description)
);
export const tablesConfigurationSelector = createSelector(
  selectUser,
  (user) =>
    Object.entries(user?.tables_configuration || {}).reduce((o, [k, v]) => {
      o[k] = JSON.parse(v);
      return o;
    }, {}) as Record<string, TableConfiguration>
);

export const analyticsGroupingsSelector = createSelector(
  selectUser,
  (user) => user?.analytics_groupings || {}
);

export const analyticsGroupingsListSelector = createSelector(
  analyticsGroupingsSelector,
  (analyticsGroupings) => {
    const list: AnalyticsGroupingItem[] = [];

    for (const department of Object.keys(analyticsGroupings)) {
      for (const category of Object.keys(analyticsGroupings[department])) {
        for (const subcategory of analyticsGroupings[department][category]) {
          list.push({
            department: capitalize(department),
            category: capitalize(category),
            subcategory: capitalize(subcategory),
          });
        }
      }
    }

    return list;
  }
);

export const userIdSelector = createSelector(
  selectUser,
  (user) => user?.uid || ""
);
export const allowedEBayIntegrationsSelector = createSelector(
  selectUser,
  (user) => user?.allowed_ebay_integrations || 1
);
export const userSalesQuickViewSelector = createSelector(
  selectUser,
  (user) => user?.salesQuickView !== false
);
export const userGetStartedStepSelector = createSelector(
  selectUser,
  (user) => user?.getStartedStep || undefined
);
export const userGetInventoryTypeSelector = createSelector(
  selectUser,
  (user) => user?.inventoryType || "accrual"
);
export const userGetStartedHasFinishedSelector = createSelector(
  userGetStartedStepSelector,
  (step) => step === "complete-set-up"
);
export const userChecklistCompletedSelector = createSelector(
  selectUser,
  (user) => user?.checklist_completed || false
);
export const userShowSetUpFeatureSelector = createSelector(
  selectUser,
  (user) => {
    if (!user) return false;
    return (
      ("checklist_completed" in user || "checklist_items" in user) &&
      !("inventoryType" in user)
    );
  }
);
export const userDontShowDialogsSelector = createSelector(
  selectUser,
  (user) => {
    if (!user) return;
    return user.dontShowDialogs || [];
  }
);

export const featureFlagSelector = createSelector(
  [(_, key: FeatureFlag) => key, selectUser],
  (key, user) => {
    return user?.featureFlags?.[key];
  }
);

export const scheduleCMappingsSelector = createSelector(
  [selectUser],
  (user) => {
    return user?.scheduleCMappings || {};
  }
);

export const scheduleCGroupingsSelector = createSelector(
  [selectUser],
  (user) => {
    return user?.scheduleCGroupings || {};
  }
);

export const userTrialEndDateSelector = createSelector([selectUser], (user) => {
  if (user?.trialEndDate)
    return (user.trialEndDate as any).toDate
      ? (user.trialEndDate as any).toDate()
      : new Date(user.trialEndDate.seconds * 1000);
});

export const userClaimsSelector = createSelector(
  [getSystemState],
  (system) => system.claims || {}
);

export const userRoleSelector = createSelector(
  [userClaimsSelector],
  (claims): "admin" | "viewer" | "user" | "accountant" => claims.role || "user"
);

/** Logged in via a support-generated Firebase custom token (admin/viewer/accountant). */
export const isSupportUserSessionSelector = createSelector(
  [userRoleSelector],
  (role): boolean =>
    role === "admin" || role === "viewer" || role === "accountant"
);

/** Internal MRG support staff (admin/viewer) — not accountants. */
export const isInternalSupportSessionSelector = createSelector(
  [userRoleSelector],
  (role): boolean => role === "admin" || role === "viewer"
);

/**
 * Any impersonation session (accountant OR internal support) — the Firebase
 * custom token carries a `support_uid` and/or `role` claim. A normal reseller
 * has neither. Used to show the in-client banner, block client-side syncs, and
 * route the activity heartbeat.
 */
export const isImpersonationSessionSelector = createSelector(
  [userClaimsSelector],
  (claims): boolean => !!claims.support_uid || !!claims.role
);

export const userCompanySelector = createSelector(
  [selectUser],
  (user) => user?.company
);

// New User Onboarding Selectors
export const userQualifyingQuestionsSelector = createSelector(
  selectUser,
  (user) => user?.qualifyingQuestions || undefined
);

export const userSetupQuestionsSelector = createSelector(selectUser, (user) => {
  const setupQuestions = user?.setupQuestions;
  if (!setupQuestions) return undefined;
  // Some (legacy/partial) accounts have a setupQuestions object that is
  // missing the array fields. Normalize them to empty arrays so consumers can
  // safely call `.some` / `.filter` / `.includes` without crashing.
  return {
    ...setupQuestions,
    sellingPlatforms: setupQuestions.sellingPlatforms ?? [],
  };
});

/** User IANA timezone from profile, else browser (`Intl`). */
export const effectiveUserTimeZoneSelector = createSelector(
  [selectUser],
  (user) => getEffectiveTimeZoneFromUser(user)
);

const dashboardDateRangeStoredSelector = createSelector(
  [selectUser],
  (user) => user?.dashboardDateRange as StoredDateRange | undefined
);

const transactionDateRangeStoredSelector = createSelector(
  [selectUser],
  (user) => user?.transactionDateRange as StoredDateRange | undefined
);

const dashboardDateRangeOptimisticSelector = createSelector(
  [getSystemState],
  (system) => !!system.dashboardDateRangeOptimistic
);

const transactionDateRangeOptimisticSelector = createSelector(
  [getSystemState],
  (system) => !!system.transactionDateRangeOptimistic
);

export const dashboardDateRangeSelector = createSelector(
  [
    dashboardDateRangeStoredSelector,
    dashboardDateRangeOptimisticSelector,
    effectiveUserTimeZoneSelector,
  ],
  (stored, optimistic, timeZone): ResolvedDateRange => {
    const clampStart = getClampStartDate(timeZone);
    const defaultPeriod = getDefaultDateRangePeriod(timeZone);
    return resolveStoredDateRange(
      stored,
      clampStart,
      defaultPeriod,
      !optimistic,
      timeZone
    );
  }
);

export const transactionDateRangeSelector = createSelector(
  [
    transactionDateRangeStoredSelector,
    transactionDateRangeOptimisticSelector,
    effectiveUserTimeZoneSelector,
  ],
  (stored, optimistic, timeZone): ResolvedDateRange => {
    const clampStart = getClampStartDate(timeZone);
    const defaultPeriod = getDefaultDateRangePeriod(timeZone);
    return resolveStoredDateRange(
      stored,
      clampStart,
      defaultPeriod,
      !optimistic,
      timeZone
    );
  }
);
// Theme Preferences Selectors
export const themeModeSelector = createSelector(
  selectUser,
  (user) => user?.themePreferences?.mode || "light"
);

export const themeUseCustomPaletteSelector = createSelector(
  selectUser,
  (user) => user?.themePreferences?.useCustomPalette || false
);

export const themeCustomLightPaletteSelector = createSelector(
  selectUser,
  (user) =>
    user?.themePreferences?.customLightPalette || { ...defaultLightPalette }
);

export const themeCustomDarkPaletteSelector = createSelector(
  selectUser,
  (user) =>
    user?.themePreferences?.customDarkPalette || { ...defaultDarkPalette }
);
